<template>
  <l-geo-json
    id="geojsonLayer"
    ref="geojsonLayer"
    :options="{ style: reactiveStyle, pointToLayer, onEachFeature }"
    :geojson="geometries"
  />
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import { LGeoJson } from 'vue2-leaflet'
import { icon as faIcon } from '@fortawesome/fontawesome-svg-core'
import { Feature } from 'geojson'
import { marker as LeafletMarker, DivIcon, LatLngExpression, PointTuple, GeoJSON, PathOptions } from 'leaflet'
import { IconDefinition, faMapPin, faMapMarkerAlt, faMapMarker } from '@fortawesome/free-solid-svg-icons'

type Icons = {
  'map-pin': IconDefinition,
  'map-marker': IconDefinition,
  'map-marker-alt': IconDefinition,
}

interface GeoJSONLayer extends Vue {
  geojsonLayer: GeoJSON,
}

const components = {
  LGeoJson,
}

// eslint-disable-next-line no-use-before-define
@Component<GeojsonLayer>({
  components,
  watch: {
    style () {
      this.$forceUpdate()
    },
    legends: {
      handler () {
        this.$forceUpdate()
      },
      deep: true,
    },
    marker () {
      this.$forceUpdate()
    },
    storyPath () {
      this.$typedStore.public.geometries.update()
    },
  },
})
export default class GeojsonLayer extends Vue {
  icons: Icons = {
    'map-pin': faMapPin,
    'map-marker': faMapMarker,
    'map-marker-alt': faMapMarkerAlt,
  }

  get config () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config
  }

  get stroke () {
    return this.config.mapConfig.style.stroke
  }

  get color () {
    return this.config.mapConfig.style.color
  }

  get weight () {
    return this.config.mapConfig.style.weight
  }

  get opacity () {
    return this.config.mapConfig.style.opacity
  }

  get dashArray () {
    return this.config.mapConfig.style.dashArray
  }

  get dashOffset () {
    return this.config.mapConfig.style.dashOffset
  }

  get fill () {
    return this.config.mapConfig.style.fill
  }

  get fillOpacity () {
    return this.config.mapConfig.style.fillOpacity
  }

  get highlightColor () {
    return this.config.mapConfig.style.highlightColor
  }

  get markerIconName () {
    return this.config.mapConfig.markerIconName
  }

  get markerCustom () {
    return this.config.mapConfig.markerCustom
  }

  get markerSize () {
    return this.config.mapConfig.markerSize
  }

  get legends () {
    return this.config.mapConfig.legends
  }

  get shpj () {
    return this.config.appConfig.geomShpProp ?? ''
  }

  get fieldMap () {
    return this.config.fieldMap
  }

  get geomConfig () {
    return this.config.geomConfig
  }

  get loadingMapData () {
    return !this.$typedStore.public.geometries.loaded
  }

  get locationData () {
    return this.$typedStore.public.geometries.locationData
  }

  get storyPath () {
    if (this.$typedStore.activeVisualisation.isPrimaryApp) {
      return this.$typedStore.public.display.storyPath
    } else {
      return this.$typedStore.public.display.secondaryStoryPath
    }
  }

  get geometries () {
    if (this.loadingMapData) {
      return []
    } else {
      return this.$typedStore.public.geometries.geojson
    }
  }

  get style () {
    return {
      stroke: this.stroke,
      weight: Number(this.weight),
      opacity: Number(this.opacity),
      dashArray: this.dashArray,
      dashOffset: this.dashOffset,
      fill: this.fill,
      fillOpacity: Number(this.fillOpacity),
      color: this.color,
    }
  }

  get marker () {
    // Convenience method to watch
    return [this.markerIconName, this.markerSize, this.markerCustom]
  }

  get getBounds () {
    return (this.$refs as unknown as GeoJSONLayer).geojsonLayer.getBounds()
  }

  reactiveStyle (feature: Feature) {
    return {
      ...this.style,
      fillColor: this.getFillColor(feature?.properties?.userproperties[this.shpj]),
    }
  }

  getFillColor (id: string) {
    const total = this.fieldMap.reduce((total, current) => current.total ? total + Number(this.locationData[id][current.column]) : total, 0)
    const result = this.legends
      .slice(0) // clones legends array
      .sort((a, b) => { // sorts by maximum to minimum
        return Number(b.range) - Number(a.range)
      })
      .find(function (legend) { // returns if true
        return legend.range <= total
      })

    if (result) { // gets the colour
      return result.colour
    }
  }

  updateSelectedGeometry (selectedGeometry: string | null | undefined, selectedGeometryName: string | null | undefined) {
    this.$emit('update-selected-geometry', selectedGeometry)
    this.$emit('update-selected-geometry-name', selectedGeometryName)
  }

  pointToLayer (geoJsonPoint: { properties: { userproperties: Record<string, string> }}, latlng: LatLngExpression) {
    const id = geoJsonPoint.properties.userproperties[this.shpj]
    const icon = faIcon(this.icons[this.markerIconName as keyof Icons], {
      attributes: {
        color: this.getFillColor(id) || '',
        height: '100%',
        width: '100%',
      },
      classes: ['map-marker-override'],
    })

    const customIcon = new DivIcon({
      html: icon.html[0],
      className: '',
      iconSize: new Array(2).fill(this.markerSize) as PointTuple,
      iconAnchor: [(this.markerSize ?? 0) / 2, (this.markerSize ?? 0)] as PointTuple,
    })
    return LeafletMarker(latlng, {
      ...this.markerCustom ? { icon: customIcon } : {},
    })
  }

  onEachFeature (feature: Feature, layer: GeoJSON) {
    const mouseover = () => {
      if ('setStyle' in layer) {
        layer.setStyle({
          ...this.reactiveStyle(feature),
          color: this.highlightColor,
        } as PathOptions)
      }
      // because geomConfig could be undefined due to Map being optional
      const selectedGeometry = feature?.properties?.userproperties[this.shpj]
      const selectedGeometryName = this.geomConfig && this.geomConfig[selectedGeometry]
      this.updateSelectedGeometry(selectedGeometry, selectedGeometryName)
    }
    const mouseout = () => {
      if ('setStyle' in layer) {
        layer.setStyle(this.reactiveStyle(feature))
      }
      this.updateSelectedGeometry(null, '')
    }
    layer.on({
      mouseover,
      mouseout,
    })
  }
}

</script>
